/**
 * Helpers para las asociaciones contenido <-> curso
 * (exam_setting_course, library_folder_course, video_folder_course).
 *
 * La jerarquia es curso > tipo de curso > modalidad > sede > turno, y cada
 * nivel solo es valido si cuelga del nivel anterior. El arbol que llega de
 * /getBasicData es una lista de nodos {id, alias, name, list}.
 *
 * Dos reglas:
 *  - Al leer: si un id guardado no existe dentro de su padre (dato corrupto en
 *    BD, o elemento con show_in_magnament = 0 que no viene en basicData) hay
 *    que devolver null en lugar de indexar con -1, que es lo que reventaba la
 *    pantalla entera.
 *  - Al escribir: cambiar un nivel invalida todos los inferiores, hay que
 *    limpiarlos o quedan ids huerfanos apuntando a otra rama.
 */
export interface CourseAssociationRow {
  courseId: number;
  courseTypeId: number;
  modalityId: number;
  siteId: number;
  turnId: number;
}

export class CourseAssociation {

  /** Hijos del nodo `id` dentro de `list`, o null si el nodo no existe / no tiene hijos. */
  static childrenOf(list: any[], id: any): any[] {
    if (list == null || id == null || id == undefined) {
      return null;
    }
    const node = list.find(x => x.id == id);
    if (node == null || node.list == null || node.list.length == 0) {
      return null;
    }
    return node.list;
  }

  static courseTypeOptions(basicData: any[], row: CourseAssociationRow): any[] {
    return this.childrenOf(basicData, row != null ? row.courseId : null);
  }

  static modalityOptions(basicData: any[], row: CourseAssociationRow): any[] {
    return this.childrenOf(this.courseTypeOptions(basicData, row), row != null ? row.courseTypeId : null);
  }

  static siteOptions(basicData: any[], row: CourseAssociationRow): any[] {
    return this.childrenOf(this.modalityOptions(basicData, row), row != null ? row.modalityId : null);
  }

  static turnOptions(basicData: any[], row: CourseAssociationRow): any[] {
    return this.childrenOf(this.siteOptions(basicData, row), row != null ? row.siteId : null);
  }

  /** Cambiar el curso invalida tipo, modalidad, sede y turno. */
  static setCourse(row: CourseAssociationRow, id: number) {
    row.courseId = id;
    row.courseTypeId = null;
    row.modalityId = null;
    row.siteId = null;
    row.turnId = null;
  }

  static setCourseType(row: CourseAssociationRow, id: number) {
    row.courseTypeId = id;
    row.modalityId = null;
    row.siteId = null;
    row.turnId = null;
  }

  static setModality(row: CourseAssociationRow, id: number) {
    row.modalityId = id;
    row.siteId = null;
    row.turnId = null;
  }

  static setSite(row: CourseAssociationRow, id: number) {
    row.siteId = id;
    row.turnId = null;
  }

  static setTurn(row: CourseAssociationRow, id: number) {
    row.turnId = id;
  }

  /**
   * Limpia los niveles que no cuelgan de su padre segun basicData. Se aplica al
   * cargar una fila para que un registro ya corrupto en BD no se vuelva a
   * guardar tal cual ni rompa la pantalla.
   *
   * Devuelve true si ha tenido que corregir algo.
   */
  static sanitize(basicData: any[], row: CourseAssociationRow): boolean {
    if (basicData == null || row == null) {
      return false;
    }
    let changed = false;

    if (row.courseId != null && basicData.find(x => x.id == row.courseId) == null) {
      // El curso no existe o no es visible en magnament: no podemos deducir
      // nada de los niveles inferiores, se limpian todos menos el curso, que se
      // deja para que el operador vea que fila estaba mal.
      changed = row.courseTypeId != null || row.modalityId != null || row.siteId != null || row.turnId != null;
      row.courseTypeId = null;
      row.modalityId = null;
      row.siteId = null;
      row.turnId = null;
      return changed;
    }

    const levels: { get: () => number, clear: () => void, options: () => any[] }[] = [
      { get: () => row.courseTypeId, clear: () => { row.courseTypeId = null; }, options: () => this.courseTypeOptions(basicData, row) },
      { get: () => row.modalityId,   clear: () => { row.modalityId = null; },   options: () => this.modalityOptions(basicData, row) },
      { get: () => row.siteId,       clear: () => { row.siteId = null; },       options: () => this.siteOptions(basicData, row) },
      { get: () => row.turnId,       clear: () => { row.turnId = null; },       options: () => this.turnOptions(basicData, row) }
    ];

    for (let i = 0; i < levels.length; i++) {
      const id = levels[i].get();
      if (id == null) {
        // Cadena con hueco: todo lo de debajo es huerfano.
        for (let j = i + 1; j < levels.length; j++) {
          if (levels[j].get() != null) {
            levels[j].clear();
            changed = true;
          }
        }
        break;
      }
      const options = levels[i].options();
      if (options == null || options.find(x => x.id == id) == null) {
        for (let j = i; j < levels.length; j++) {
          if (levels[j].get() != null) {
            levels[j].clear();
            changed = true;
          }
        }
        break;
      }
    }

    return changed;
  }

  static sanitizeAll(basicData: any[], rows: CourseAssociationRow[]): boolean {
    if (rows == null) {
      return false;
    }
    let changed = false;
    rows.forEach(r => {
      if (this.sanitize(basicData, r)) {
        changed = true;
      }
    });
    return changed;
  }
}
